import { MouseEventHandler, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { useNavigate, useParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import { SINGLE_SECOND_INTERVAL } from '../../constants'
import useOverviewContext from '../../context/OverviewContextProvider'
import useSelectionContext from '../../context/SelectionContextProvider'
import { useInterval, useIsMobile, useKeyboardShortcut, usePreviewMode, useShowDetailsPane } from '../../hooks'
import useGetSortedFolderTasks from '../../hooks/useGetSortedFolderTasks'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import Log from '../../services/api/log'
import { useMarkTaskDoneOrDeleted, useModifyTask } from '../../services/api/tasks.hooks'
import { useGetTasksV4 } from '../../services/api/tasks.hooks'
import { Border, Colors, Dimensions, Spacing, Typography } from '../../styles'
import { icons } from '../../styles/images'
import { shouldWaitForMotion } from '../../styles/motion'
import { DropType, TTaskV4 } from '../../utils/types'
import Domino from '../atoms/Domino'
import DueDate from '../atoms/DueDate'
import Flex from '../atoms/Flex'
import { Icon } from '../atoms/Icon'
import { MeetingStartText } from '../atoms/MeetingStartText'
import TaskTemplate from '../atoms/TaskTemplate'
import GTButton from '../atoms/buttons/GTButton'
import MarkTaskDoneButton from '../atoms/buttons/MarkTaskDoneButton'
import { LabelSmall } from '../atoms/typography/Typography'
import { useCalendarContext } from '../calendar/CalendarContext'
import { useOptionalGoalCreation } from '../goals/creation/shared/GoalCreationContext'
import PriorityDropdown from '../radix/PriorityDropdown'
import TaskContextMenuWrapper from '../radix/TaskContextMenuWrapper'
import Tip from '../radix/Tip'
import { ReorderActions } from '../radix/reorderMenuItems'
import ItemContainer from './ItemContainer'
import { useGetRecurringTaskTemplateFromId } from './recurring-tasks/recurringTasks.utils'

const MarginRight = styled.div`
    margin-right: ${Spacing._8};
`
export const GTButtonHack = styled(GTButton)`
    width: 20px !important;
    padding: ${Spacing._4} !important;
    box-sizing: border-box;
    max-width: 20px;
`
const RightContainer = styled.span`
    display: flex;
    align-items: center;
    gap: ${Spacing._12};
    margin-left: auto;
    min-width: fit-content;
`
const GoalTag = styled.span`
    display: flex;
    align-items: center;
    gap: ${Spacing._4};
    max-width: 160px;
    color: ${Colors.text.muted};
    ${Typography.label.small};
    span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`
const Title = styled.span<{ $isOverviewRow: boolean }>`
    margin-left: ${({ $isOverviewRow }) => ($isOverviewRow ? Spacing._12 : Spacing._8)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${Typography.body.medium};
    color: ${Colors.text.ink};
    padding-right: ${Spacing._8};
`
export const PositionedDomino = styled(Domino)`
    margin-right: ${Spacing._8};
`
const OverviewLeadingControl = styled.div`
    position: absolute;
    z-index: 1;
    top: 50%;
    right: 100%;
    display: flex;
    align-items: center;
    transform: translateY(-50%);
`
const TaskItemContainer = styled(ItemContainer)<{ $isOverviewRow: boolean }>`
    ${({ $isOverviewRow }) =>
        $isOverviewRow &&
        `
        /* ItemContainer's border is always present, so 7px of padding produces the same 8px
           content inset as a routine row without shrinking the selected/hovered row box. */
        padding: 0 calc(${Spacing._8} - ${Border.stroke.medium});
    `}
`
const PriorityTarget = styled.span<{ $isPlaceholder?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;

    ${Dimensions.mediaQuery.phone} {
        min-width: ${Spacing._44};
        min-height: ${Spacing._44};

        > div,
        button {
            min-width: ${Spacing._44};
            min-height: ${Spacing._44};
        }

        ${({ $isPlaceholder }) => $isPlaceholder && 'opacity: 0.45;'}
    }
`

interface TaskProps {
    task: TTaskV4
    dragDisabled?: boolean
    // Hides the reorder grip while leaving the row draggable onto folders and the calendar.
    reorderDisabled?: boolean
    dropType?: DropType
    index?: number
    sectionScrollingRef?: MutableRefObject<HTMLDivElement | null>
    isSelected: boolean
    link: string
    meetingPreparationStartTime?: DateTime
    shouldScrollToTask?: boolean
    setShouldScrollToTask?: (shouldScrollToTask: boolean) => void
    onMarkTaskDone?: (id: string) => void
    reorder?: ReorderActions
    isOverviewRow?: boolean
    animateSelection?: boolean
}

const CLIPPING_OVERFLOW = /^(auto|clip|hidden|scroll)$/

const isVisibleWithinClippingAncestors = (element: HTMLElement) => {
    const view = element.ownerDocument.defaultView
    const clientRects = Array.from(element.getClientRects())
    if (!view || clientRects.length === 0) return false

    let clipTop = 0
    let clipRight = view.innerWidth
    let clipBottom = view.innerHeight
    let clipLeft = 0

    for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
        const style = view.getComputedStyle(ancestor)
        const clipsX = CLIPPING_OVERFLOW.test(style.overflowX)
        const clipsY = CLIPPING_OVERFLOW.test(style.overflowY)
        if (!clipsX && !clipsY) continue

        const ancestorRect = ancestor.getBoundingClientRect()
        if (clipsX) {
            clipLeft = Math.max(clipLeft, ancestorRect.left)
            clipRight = Math.min(clipRight, ancestorRect.right)
        }
        if (clipsY) {
            clipTop = Math.max(clipTop, ancestorRect.top)
            clipBottom = Math.min(clipBottom, ancestorRect.bottom)
        }
    }

    return clientRects.some(
        (rect) =>
            rect.width > 0 &&
            rect.height > 0 &&
            rect.right > clipLeft &&
            rect.left < clipRight &&
            rect.bottom > clipTop &&
            rect.top < clipBottom
    )
}

const Task = ({
    task,
    dragDisabled,
    reorderDisabled,
    dropType = DropType.TASK,
    index,
    sectionScrollingRef,
    isSelected,
    link,
    shouldScrollToTask,
    setShouldScrollToTask,
    onMarkTaskDone,
    reorder,
    isOverviewRow = false,
    animateSelection,
}: TaskProps) => {
    const navigate = useNavigate()
    const observer = useRef<IntersectionObserver>()
    const overviewRowRef = useRef<HTMLDivElement | null>(null)
    const isScrolling = useRef<boolean>(false)
    const [isHovered, setIsHovered] = useState(false)
    const [meetingStartText, setMeetingStartText] = useState<string | null>(null)
    const [isMeetingTextColored, setIsMeetingTextColor] = useState<boolean>(false)
    const { meeting_preparation_params } = task
    const dateTimeStart = DateTime.fromISO(task.meeting_preparation_params?.datetime_start || '')
    const dateTimeEnd = DateTime.fromISO(meeting_preparation_params?.datetime_end || '')
    const { mutate: markTaskDoneOrDeleted } = useMarkTaskDoneOrDeleted()
    const { setCalendarType, setDate, dayViewDate } = useCalendarContext()
    const showDetailsPane = useShowDetailsPane()
    const { mutate: modifyTask } = useModifyTask()
    const { onClickHandler: onMultiSelectClick, isTaskSelected: isTaskMultiSelected } = useSelectionContext()
    const { task: idTaskRoute, overviewItemId } = useParams()
    const { data: allTasks } = useGetTasksV4()
    const isMobile = useIsMobile()
    const { registerTaskExit, startTaskExits } = useOverviewContext()
    const goals = useOptionalGoalCreation()
    const goalId = goals?.goalIdForTask(task.id, task.optimisticId)
    const goal = goalId ? goals?.allGoals.find((g) => g.id === goalId) : undefined

    const { inMultiSelectMode } = useSelectionContext()

    const sortedTasks = useGetSortedFolderTasks(task.id_folder ?? '')

    useInterval(() => {
        if (!meeting_preparation_params) return
        const minutesToStart = Math.ceil(dateTimeStart.diffNow('minutes').minutes)
        const minutesToEnd = Math.ceil(dateTimeEnd.diffNow('minutes').minutes)

        if (minutesToStart < 0 && minutesToEnd > 0) {
            setMeetingStartText('Meeting is now')
            setIsMeetingTextColor(true)
        } else if (minutesToStart < 0 && minutesToEnd < 0) {
            setMeetingStartText(null)
        } else if (minutesToStart <= 30) {
            const minutesToStartText = minutesToStart === 1 ? 'minute' : 'minutes'
            setMeetingStartText(`Starts in ${minutesToStart} ${minutesToStartText}`)
            setIsMeetingTextColor(true)
        } else {
            setMeetingStartText(dateTimeStart.toLocaleString(DateTime.TIME_SIMPLE))
            setIsMeetingTextColor(false)
        }
    }, SINGLE_SECOND_INTERVAL)

    // Add event listener to check if scrolling occurs in task section
    useEffect(() => {
        const setScrollTrue = () => {
            isScrolling.current = true
        }
        sectionScrollingRef?.current?.addEventListener('scroll', setScrollTrue)
        return () => {
            sectionScrollingRef?.current?.removeEventListener('scroll', setScrollTrue)
        }
    }, [])

    //If task selection changes, re-enable auto-scrolling for task section
    useEffect(() => {
        if (sectionScrollingRef?.current) {
            isScrolling.current = false
        }
    }, [isSelected])

    //Auto-scroll to task if it is selected and out of view
    const elementRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (observer.current) observer.current.disconnect()
            if (!node) return
            observer.current = new IntersectionObserver(
                (entries) => {
                    if (shouldScrollToTask && !entries[0].isIntersecting && isSelected && !isScrolling.current) {
                        node.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    }
                    if (setShouldScrollToTask) setShouldScrollToTask(false)
                },
                { threshold: 1.0 }
            )
            observer.current.observe(node)
        },
        [isSelected, isScrolling.current, shouldScrollToTask]
    )

    const onClick = useCallback<MouseEventHandler>(
        (e) => {
            if (e.metaKey || e.shiftKey) return
            navigate(link)
            Log(`task_select__${link}`)
            if (!showDetailsPane && isSelected) {
                setCalendarType('day')
                setDate(dayViewDate)
            }
        },
        [link, isSelected, dayViewDate, inMultiSelectMode, showDetailsPane]
    )

    const isReadOnly = useIsReadOnly()
    const [, drag, dragPreview] = useDrag(
        () => ({
            type: dropType,
            item: { id: task.id, sectionId: task.id_folder, task },
            canDrag: !dragDisabled && !isReadOnly,
        }),
        [task, index, dragDisabled, isReadOnly]
    )

    // hide default drag preview
    useEffect(() => {
        dragPreview(getEmptyImage())
    }, [])

    const overviewDragRef = useCallback(
        (node: HTMLDivElement | null) => {
            overviewRowRef.current = node
            drag(node)
            elementRef(node)
        },
        [drag, elementRef]
    )

    const [isVisible, setIsVisible] = useState(true)
    const taskFadeOut = useCallback(() => {
        if (isOverviewRow) startTaskExits([task.id])
        else if (task.id_folder) setIsVisible(task.is_done)
        onMarkTaskDone?.(task.id)
    }, [isOverviewRow, onMarkTaskDone, startTaskExits, task.id, task.id_folder, task.is_done])
    const startExitAnimation = useCallback(() => {
        if (!isOverviewRow || task.is_deleted || !task.id_folder) return false
        // Mobile details keep the list pane mounted under `display: none`, and desktop rows can be
        // outside the list's scrollport. Do not delay the mutation for an exit the user cannot see;
        // another visible overview copy can still request the shared animation.
        if (!overviewRowRef.current || !isVisibleWithinClippingAncestors(overviewRowRef.current)) return false
        setIsVisible(false)
        return shouldWaitForMotion()
    }, [isOverviewRow, task.is_deleted, task.id_folder])
    useEffect(() => {
        if (!isOverviewRow) return
        return registerTaskExit(task.id, startExitAnimation)
    }, [isOverviewRow, registerTaskExit, startExitAnimation, task.id])
    const startRegisteredExitAnimation = useCallback(() => startTaskExits([task.id]), [startTaskExits, task.id])

    const dueDate = DateTime.fromISO(task.due_date)
    const [contextMenuOpen, setContextMenuOpen] = useState(false)

    const deleteTask = useCallback(() => {
        markTaskDoneOrDeleted(
            { id: task.id, isDeleted: true, waitForAnimation: startRegisteredExitAnimation() },
            task.optimisticId
        )
    }, [task, startRegisteredExitAnimation])

    useKeyboardShortcut('deleteTask', deleteTask, !isSelected)
    const { isPreviewMode } = usePreviewMode()

    const recurringTaskTemplate = useGetRecurringTaskTemplateFromId(task.recurring_task_template_id)

    const subtasks = allTasks?.filter((t) => t.id_parent === task.id && !t.is_deleted)

    const eventMovedOrDeleted = isPreviewMode && task.meeting_preparation_params?.event_moved_or_deleted
    const eventWarning = eventMovedOrDeleted ? (
        <Tip content="Event has been moved or deleted">
            <Icon icon={icons.warning} color="red" />
        </Tip>
    ) : null

    const getPriorityDropdown = () => {
        if (Number.isInteger(task.priority_normalized)) {
            if (task.priority_normalized === 0 && !isMobile) return null

            return (
                <PriorityTarget $isPlaceholder={task.priority_normalized === 0}>
                    <PriorityDropdown
                        value={task.priority_normalized}
                        onChange={(priority) =>
                            modifyTask({ id: task.id, priorityNormalized: priority }, task.optimisticId)
                        }
                        condensedTrigger
                    />
                </PriorityTarget>
            )
        }
    }

    return (
        <TaskContextMenuWrapper
            task={task}
            onOpenChange={setContextMenuOpen}
            reorder={reorder}
            onDeleteStart={startRegisteredExitAnimation}
        >
            <TaskTemplate
                ref={isOverviewRow ? overviewDragRef : elementRef}
                isVisible={isVisible}
                data-removing={!isVisible ? '' : undefined}
                onMouseLeave={() => setIsHovered(false)}
                onMouseEnter={() => setIsHovered(true)}
                onClick={(e) =>
                    onMultiSelectClick(
                        e,
                        task.id,
                        task.is_deleted || task.is_done,
                        (idTaskRoute || overviewItemId) ?? '',
                        sortedTasks
                    )
                }
            >
                {isOverviewRow && (
                    <OverviewLeadingControl>
                        {eventWarning ?? <Domino isVisible={isHovered && !dragDisabled && !reorderDisabled} />}
                    </OverviewLeadingControl>
                )}
                <TaskItemContainer
                    $isOverviewRow={isOverviewRow}
                    isSelected={isSelected}
                    isMultiSelected={isTaskMultiSelected(task.id)}
                    onClick={onClick}
                    ref={isOverviewRow ? undefined : drag}
                    forceHoverStyle={contextMenuOpen}
                    animateSelection={animateSelection}
                >
                    {!isOverviewRow &&
                        (eventWarning ? (
                            <MarginRight>{eventWarning}</MarginRight>
                        ) : (
                            // Carries its own spacing so it collapses completely when hidden on touch,
                            // rather than leaving an empty wrapper holding the gutter open.
                            <PositionedDomino isVisible={isHovered && !dragDisabled && !reorderDisabled} />
                        ))}
                    <MarkTaskDoneButton
                        taskId={task.id}
                        sectionId={task.id_folder}
                        isDone={task.is_done}
                        isSelected={isSelected}
                        isDisabled={!!task.optimisticId || task.is_deleted}
                        onMarkComplete={taskFadeOut}
                        optimsticId={task.optimisticId}
                    />
                    <Title $isOverviewRow={isOverviewRow} title={task.title}>
                        {task.title}
                    </Title>
                    <RightContainer>
                        {goal && (
                            <GoalTag title={`Goal: ${goal.title}`}>
                                <Icon icon={icons.check_circle_wavy} color="gray" size="small" />
                                <span>{goal.title}</span>
                            </GoalTag>
                        )}
                        {recurringTaskTemplate && <Icon icon={icons.arrows_repeat} />}
                        <DueDate date={dueDate} isDoneOrDeleted={task.is_done || task.is_deleted} />
                        {getPriorityDropdown()}
                        {subtasks && subtasks.length > 0 && (
                            <Flex gap={Spacing._4}>
                                <Icon icon={icons.subtask} />
                                <LabelSmall>{subtasks.length}</LabelSmall>
                            </Flex>
                        )}
                        {meetingStartText && (
                            <MeetingStartText isTextColored={isMeetingTextColored}>{meetingStartText}</MeetingStartText>
                        )}
                    </RightContainer>
                </TaskItemContainer>
            </TaskTemplate>
        </TaskContextMenuWrapper>
    )
}

export default Task
